import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@entry/navigation";
import styled from "styled-components";

import { logo } from "@components/FeaturedInSection/content";
import { AnalyticsEvent, Routes } from "@ctypes";
import { ENTRY_VIDEO_FAMILY, entryColors, entryFont, prefersReducedMotion } from "@entry/tokens";
import { EntryBody, EntryCta, EntryGhostCta, EntryTitle } from "@entry/ui";
import { useEntryAnimation } from "@entry/useEntryAnimation";
import { Translations, i18n } from "@i18n";
import { Analytics, Intercom } from "@services";
import { Screen, useEntryPageAnalytics } from "./shared";

/**
 * The design is a fixed 1728x1117 frame; no real viewport is that tall. Each
 * vertical value is therefore its own share of that canvas in `vh`, and since
 * the design's parts sum to 1117px those shares sum to ~100vh — the stack
 * fills any viewport height instead of overrunning it, which is why nothing
 * here needs to be sticky. Keep new values on this scheme: a raw px margin
 * breaks the budget and the composition starts scrolling again.
 *
 * `vwCap` ceilings a value against width too, for things that would otherwise
 * overflow sideways on a tall narrow window. HEADROOM damps every share below
 * its literal one to leave room for the values that bottom out at their floor
 * on a short viewport (chiefly the 44px minimum CTA height); undamped, the
 * stack tips into a scroll around 820px.
 */
const DESIGN_HEIGHT = 1117;
const HEADROOM = 0.95;

const scaled = (designPx: number, floorPx: number, vwCap?: number) => {
  const vh = ((designPx / DESIGN_HEIGHT) * 100 * HEADROOM).toFixed(2);
  const preferred = vwCap ? `min(${vh}vh, ${vwCap}vw)` : `${vh}vh`;
  return `clamp(${floorPx}px, ${preferred}, ${designPx}px)`;
};

/* Screen's own 40px top padding would double-count the design's first gap,
   which CircleVideo's margin already carries. */
const Landing = styled(Screen)`
  padding: 0;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
`;

const CircleVideo = styled.video`
  width: ${scaled(216, 120, 45)};
  height: ${scaled(216, 120, 45)};
  margin-top: ${scaled(137, 20)};
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${entryColors.terracotta};
`;

const Laurels = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${scaled(16, 10)};
  margin-top: ${scaled(73, 18)};
`;

/* One branch, mirrored for the far side: the source artwork's two halves
   carry unequal padding, so using both would sit the right branch visibly
   nearer the copy than the left. */
const LaurelBranch = styled.span<{ $flip?: boolean }>`
  display: block;
  flex: none;
  height: ${scaled(94, 52)};
  aspect-ratio: 164 / 281;
  background-image: url("/images/entry/laurel-branch.png");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  transform: ${({ $flip }) => ($flip ? "scaleX(-1)" : "none")};
`;

/* No gap: the asset's own padding (ink stops 10px short of its 164px frame)
   is already the spacing the design shows. Adding one leaves the copy
   floating in the middle of the wreath. */
const LaurelWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
`;

const LaurelText = styled.div`
  position: relative;
  text-align: center;
  font-family: ${entryFont.body};

  span {
    display: block;
    font-size: ${scaled(12, 10)};
    font-weight: 700;
    letter-spacing: -0.09px;
    color: rgba(255, 255, 255, 0.8);
  }

  strong {
    display: block;
    margin-top: 3px;
    font-size: ${scaled(15, 12)};
    font-weight: 900;
    text-transform: uppercase;
    color: ${entryColors.textPrimary};
  }
`;

const LaurelBadge = ({ caption, value }: { caption: string; value: string }) => (
  <LaurelWrapper>
    <LaurelBranch aria-hidden="true" />
    <LaurelText>
      <span>{caption}</span>
      <strong>{value}</strong>
    </LaurelText>
    <LaurelBranch aria-hidden="true" $flip />
  </LaurelWrapper>
);

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: ${scaled(25, 10)};
  text-align: center;
`;

const LandingTitle = styled(EntryTitle)`
  font-size: ${scaled(50, 26, 8)};
  font-weight: 700;
  line-height: 1.34;
  letter-spacing: -0.5px;
`;

const Subtitle = styled(EntryBody)`
  margin-top: ${scaled(14, 8)};
  font-family: ${entryFont.title};
  font-size: ${scaled(24, 15, 4)};
  font-weight: 700;
  line-height: 1.34;
  color: ${entryColors.textPrimary};
`;

/* Static, not sticky — a pinned band here is what used to ride up over the
   hero video as the viewport shortened. */
const CtaBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${scaled(49, 18)};
`;

const ctaSize = `
  width: min(354px, 100%);
  max-width: 354px;
  height: ${scaled(52, 44)};
  font-size: ${scaled(18, 15, 1.2)};
`;

const LandingCta = styled(EntryCta)`
  ${ctaSize}
  background: rgba(205, 128, 84, 0.75);
  border: none;
  box-shadow: 0 10px 20px -10px rgba(0, 0, 0, 0.25);
`;

const LandingGhostCta = styled(EntryGhostCta)`
  ${ctaSize}
  margin-top: ${scaled(13, 6)};
  color: #f5f0eb;
`;

const FeaturedBy = styled.p`
  margin: ${scaled(66, 22)} 0 0;
  font-family: ${entryFont.body};
  font-size: ${scaled(12, 11)};
  font-weight: 800;
  line-height: 1.7;
  letter-spacing: -0.37px;
  text-transform: uppercase;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  opacity: 0.6;
`;

const PressRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${scaled(24, 10)} clamp(20px, 3.4vw, 58px);
  margin-top: ${scaled(24, 10)};
  padding-bottom: ${scaled(59, 16)};
  opacity: 0.5;

  /* Shared logos ship at a fixed 48px tall; all carry a viewBox, so setting
     both axes rescales them to the 26-35px the design draws. */
  svg {
    display: block;
    height: ${scaled(34, 16, 2.4)};
    width: auto;
  }
`;

/* Matched to EntryStepLayout's SupportChatBtn so it doesn't jump when the
   flow advances off the landing. */
const ChatButton = styled.button`
  position: fixed;
  top: 26px;
  right: 26px;
  z-index: 3;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  line-height: 0;

  svg {
    width: 44px;
    height: 44px;
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
    border-radius: 50%;
  }
`;

const SupportChatIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path
      d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z"
      fill="#DF8F68" fillOpacity="0.3"
    />
    <path
      d="M24 0.7061C36.865 0.7061 47.2939 11.135 47.2939 24C47.2939 36.865 36.865 47.2939 24 47.2939C11.135 47.2939 0.7061 36.865 0.7061 24C0.7061 11.135 11.135 0.7061 24 0.7061Z"
      stroke="url(#sc_grad1)" strokeOpacity="0.35" strokeWidth="1.41176"
    />
    <path
      d="M24 0.7061C36.865 0.7061 47.2939 11.135 47.2939 24C47.2939 36.865 36.865 47.2939 24 47.2939C11.135 47.2939 0.7061 36.865 0.7061 24C0.7061 11.135 11.135 0.7061 24 0.7061Z"
      stroke="url(#sc_grad2)" strokeOpacity="0.35" strokeWidth="1.41176"
    />
    <g clipPath="url(#sc_clip)">
      <path
        d="M24.6282 13.1976C23.8141 13.1357 23.0627 13.2127 22.2626 13.3373C19.3655 13.7884 16.8275 15.3448 15.1155 17.7338C13.5358 19.9201 12.9077 22.6523 13.3739 25.3089C13.7979 27.8532 15.4641 30.4343 17.5699 31.904C20.1404 33.6982 22.9181 34.2446 25.9633 33.7341C26.822 33.5319 27.3711 33.3672 28.1918 33.0674C28.4252 33.1333 29.017 33.4056 29.2537 33.5103L31.1785 34.3688C31.5314 34.5266 31.8518 34.6808 32.2255 34.788C32.6479 34.9586 33.2976 34.5284 33.1843 34.0529C32.8843 32.7942 32.4649 31.4608 32.1098 30.2175C32.1338 30.1993 32.1573 30.1805 32.1803 30.1611C32.4016 29.9737 32.7522 29.5287 32.9234 29.2945C33.99 27.808 34.6222 26.0542 34.7494 24.2291C35.0303 20.6768 33.1691 17.1449 30.278 15.1406C28.5504 13.943 26.7088 13.3573 24.6282 13.1976Z"
        stroke="#F5F0EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
      <path
        d="M18.457 25.2383C19.0949 26.1941 19.9283 26.9719 20.89 27.509C21.8517 28.046 22.9146 28.3271 23.9929 28.3296C25.0712 28.332 26.1345 28.0558 27.097 27.5232C28.0594 26.9906 28.894 26.2166 29.5332 25.2637"
        stroke="#F5F0EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </g>
    <defs>
      <linearGradient id="sc_grad1" x1="0" y1="0" x2="18.3529" y2="18.3529" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="sc_grad2" x1="48" y1="48" x2="29.6471" y2="29.6471" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </linearGradient>
      <clipPath id="sc_clip">
        <rect width="24" height="24" fill="white" transform="matrix(-1 0 0 1 36 12)" />
      </clipPath>
    </defs>
  </svg>
);

export const WelcomeScreen = () => {
  const navigate = useNavigate();
  const [videoFailed, setVideoFailed] = useState(false);
  const showVideo = !videoFailed && !prefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEntryPageAnalytics(Routes.Auth);

  const { contentStyle, formStyle, ctaStyle, animateOut } = useEntryAnimation();

  // Autoplay is best-effort, not guaranteed — browsers can refuse it (Low
  // Power Mode, memory pressure, competing with the EntryShell background
  // video). Explicitly calling play() and catching a rejection lets us fall
  // back to hiding the video, matching EntryShell's BackgroundVideo, rather
  // than leaving it sitting paused on its first frame indefinitely.
  useEffect(() => {
    if (!showVideo) return;

    const playPromise = videoRef.current?.play();
    playPromise?.catch(() => setVideoFailed(true));
  }, [showVideo]);

  const handleCreateAccount = () => {
    Analytics.track(AnalyticsEvent.AuthPrimaryCTAPressed);
    animateOut(() => navigate(Routes.GetStarted));
  };

  const handleHaveAccount = () => {
    Analytics.track(AnalyticsEvent.AuthSecondaryCTAPressed);
    animateOut(() => navigate(Routes.Login));
  };

  const { forbes, usnews, yahoo, babylist } = logo.dark;

  return (
    <Landing>
      <ChatButton type="button" aria-label="Chat with support" onClick={() => Intercom.showChat()}>
        <SupportChatIcon />
      </ChatButton>

      <Hero style={contentStyle}>
        {showVideo && (
          <CircleVideo
            ref={videoRef}
            src={ENTRY_VIDEO_FAMILY}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            onError={() => setVideoFailed(true)}
          />
        )}
        <Laurels>
          <LaurelBadge
            caption={i18n.t(Translations.EntryWelcomeTrustFamiliesCaption)}
            value={i18n.t(Translations.EntryWelcomeTrustFamiliesValue)}
          />
          <LaurelBadge
            caption={i18n.t(Translations.EntryWelcomeTrustInvestCaption)}
            value={i18n.t(Translations.EntryWelcomeTrustInvestValue)}
          />
        </Laurels>
      </Hero>

      <TitleBlock style={formStyle}>
        <LandingTitle as="h1" $align="center">
          <b>{i18n.t(Translations.EntryWelcomeTitle)}</b>
        </LandingTitle>
        <Subtitle $align="center">{i18n.t(Translations.EntryWelcomeSubtitle)}</Subtitle>
      </TitleBlock>

      <CtaBlock style={ctaStyle}>
        <LandingCta type="button" $variant="terracotta" onClick={handleCreateAccount}>
          {i18n.t(Translations.EntryWelcomePrimaryCTA)}
        </LandingCta>
        <LandingGhostCta type="button" onClick={handleHaveAccount}>
          {i18n.t(Translations.EntryWelcomeSecondaryCTA)}
        </LandingGhostCta>

        <FeaturedBy>{i18n.t(Translations.EntryWelcomeFeaturedBy)}</FeaturedBy>
        <PressRow aria-label={i18n.t(Translations.EntryWelcomeFeaturedBy)}>
          {forbes()}
          {usnews()}
          {yahoo()}
          {babylist()}
        </PressRow>
      </CtaBlock>
    </Landing>
  );
};
